import { Link } from 'react-router-dom'

import { PUBLISH_TARGETS, PUBLISH_TARGET_LABELS } from '@/api/listings'
import { NotLivePage, useNotLivePage } from '@/app/notLive'
import { Badge, Card, EmptyState, LoadingBlock } from '@/design'
import { useFilterSearch } from '@/lib/useGlobalFilters'

import { useListings } from './hooks'
import './listings.css'

/** Where every listing has been published, and the log of what happened (pivot doc §6 Stage 3). */
export function PublishingPage() {
  const notLive = useNotLivePage('listings')
  const listings = useListings()
  const search = useFilterSearch()
  const rows = listings.data?.data ?? []
  const log = rows.flatMap((listing) => listing.publishLog).sort((a, b) => b.at.localeCompare(a.at))

  if (notLive) return <NotLivePage />

  return (
    <div className="lst-form-page">
      <Card title="Destinations" subtitle="Where each listing has been published, by destination." busy={listings.isFetching}>
        {listings.isPending ? <LoadingBlock rows={4} /> : null}
        {listings.isError ? <EmptyState variant="outage" compact description="Listings could not be loaded." /> : null}
        {listings.data && rows.length === 0 ? (
          <EmptyState variant="no-data" compact title="No listings yet" description="Create a listing to publish it somewhere." />
        ) : null}
        {listings.data && rows.length > 0 ? (
          <div className="ci-table-wrap" role="region" aria-label="Publishing destinations by listing" tabIndex={0}>
            <table className="ci-table">
              <caption className="ci-visually-hidden">Publishing destinations by listing</caption>
              <thead>
                <tr>
                  <th scope="col">Listing</th>
                  {PUBLISH_TARGETS.map((target) => (
                    <th scope="col" key={target}>
                      {PUBLISH_TARGET_LABELS[target]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((listing) => (
                  <tr key={listing.id}>
                    <th scope="row">
                      <Link to={`/competitions/listings/${listing.id}${search}`}>
                        {listing.copy?.title ?? listing.prize.prizeName}
                      </Link>
                    </th>
                    {PUBLISH_TARGETS.map((target) => {
                      const state = listing.publishing.find((candidate) => candidate.target === target)
                      const published = state?.status === 'published'
                      // Same rule as the listing form: a published destination whose approval was reset
                      // since is stale, and this screen exists to show what is actually live.
                      const stale = published && state?.stale === true
                      return (
                        <td key={target}>
                          {stale ? (
                            <Badge tone="warning">Needs republish</Badge>
                          ) : (
                            <Badge tone={published ? 'positive' : 'neutral'}>{published ? 'Published' : 'Not published'}</Badge>
                          )}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>

      <Card title="Publish log" subtitle="What was published, where, and when.">
        {log.length === 0 ? (
          <EmptyState
            variant="no-data"
            compact
            title="Nothing published yet"
            description="Publish an approved listing to a destination to see it logged here."
          />
        ) : (
          <ul className="lst-log" aria-label="Publish log">
            {log.map((entry) => (
              <li key={entry.id}>
                <strong>{entry.listingTitle}</strong> → {PUBLISH_TARGET_LABELS[entry.target]} · {entry.message}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  )
}
